import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';

import { DiagStackGenericResponse } from '@vi-iot/client';

import { AppState } from '@store';

import { ActiveErrorData } from '../active-errors/active-errors.model';
import { ActiveErrorsSelectors } from '../active-errors/active-errors.selectors';

import { ActiveDtc, DtcType } from './diag-stack-dtc.model';
import { DiagStackDtcState } from './diag-stack-dtc.state';

export namespace DiagStackDtcSelectors {
  const diagStackDtc = (state: AppState): DiagStackDtcState => state.diagStackDtc;

  export const getActiveDtcError = createSelector(
    diagStackDtc,
    (state: DiagStackDtcState): any => state.activeDtcError
  );

  export const getActiveDtcResponse = createSelector(
    diagStackDtc,
    (state: DiagStackDtcState): DiagStackGenericResponse => state.activeDtcResponse
  );

  export const isActiveDtcLoading = createSelector(
    diagStackDtc,
    (state: DiagStackDtcState): boolean => state.activeDtcLoading
  );

  export const getAllActiveDtc = createSelector(
    diagStackDtc,
    (state: DiagStackDtcState): ActiveDtc[] => {
      if (state?.activeDtcResponse?.type !== DtcType.ReadActiveDTCVDDs) {
        return [];
      }

      const errorStructure = state.activeDtcResponse.structure.find((s) => s.severity === 'ERROR');

      const result: ActiveDtc[] = [];
      eachRecursive(errorStructure, errorStructure.ecu, result);

      return result;
    }
  );

  export const getAllActiveDtcLogicalLink = createSelector(
    diagStackDtc,
    (state: DiagStackDtcState): string => {
      if (state?.activeDtcResponse?.type !== DtcType.ReadActiveDTCVDDs) {
        return null;
      }

      return state.activeDtcResponse.structure.find((s) => s.severity === 'ERROR')?.ecu;
    }
  );

  export const getActiveDtcForSelectedError = createSelector(
    getAllActiveDtc,
    ActiveErrorsSelectors.getSelectedActiveError,
    (getAllActiveDtcSelector: ActiveDtc[], activeError: ActiveErrorData): ActiveDtc => {
      activeError.errorCode.replace('.', '_');

      if (!getAllActiveDtcSelector) {
        return null;
      }

      return getAllActiveDtcSelector.find((activeDtc) =>
        activeDtc.dtcNameTranslationId.length > 3
          ? activeDtc.dtcNameTranslationId.substring(3)
          : null === activeError.errorCode
      );
    }
  );

  export function getActiveDtc(
    errorKey: string
  ): MemoizedSelector<AppState, ActiveDtc, DefaultProjectorFn<ActiveDtc>> {
    return createSelector(
      getAllActiveDtc,
      (getAllActiveDtcSelector: ActiveDtc[]): ActiveDtc => {
        errorKey.replace('.', '_');

        if (!getAllActiveDtcSelector) {
          return null;
        }

        return getAllActiveDtcSelector.find((activeDtc) =>
          activeDtc.dtcNameTranslationId.length > 3
            ? activeDtc.dtcNameTranslationId.substring(3)
            : null === errorKey
        );
      }
    );
  }

  function eachRecursive(
    diagStackErrorStructure: any,
    logicalLink: string,
    result: ActiveDtc[]
  ): void {
    const isDtcDefinition =
      diagStackErrorStructure.hasOwnProperty('value') &&
      diagStackErrorStructure.hasOwnProperty('valuetranslationid') &&
      diagStackErrorStructure.hasOwnProperty('name') &&
      diagStackErrorStructure.name === 'PA_DTC';
    if (isDtcDefinition) {
      const alreadyHasDtc = result.find((r) => r.dtcName === diagStackErrorStructure.value);
      if (!alreadyHasDtc) {
        result.push({
          dtcName: diagStackErrorStructure.value,
          dtcNameTranslationId: diagStackErrorStructure.valuetranslationid,
          logicalLink,
        });
      }
    }
    for (let key in diagStackErrorStructure) {
      if (
        diagStackErrorStructure[key] !== null &&
        typeof diagStackErrorStructure[key] === 'object'
      ) {
        eachRecursive(diagStackErrorStructure[key], logicalLink, result);
      }
    }
  }
}
